import React, { useState, useEffect, useCallback, useRef } from 'react';
import { timeSlots as allTimeSlotsConfig, SCHEDULE_START_MINUTE, SCHEDULE_END_MINUTE } from '../config/constants';
import { TEACHER_PREFERENCE_STATUS } from '../constants/preferences';
import { getStatusForSlot, timeToMinutes, parseSlotKey, formatTimeFromMinutes } from '../utils/helpers.ts';
import Preference from '../models/Preference.ts';
import { useActiveDays } from '../hooks/useActiveDays';

// Color Helpers
const getStatusColor = (status) => {
    switch (status) {
        case TEACHER_PREFERENCE_STATUS.PREFER_AWAY: return 'bg-orange-200 hover:bg-orange-300';
        case TEACHER_PREFERENCE_STATUS.CANNOT: return 'bg-red-200 hover:bg-red-300';
        case TEACHER_PREFERENCE_STATUS.FREE: default: return 'bg-green-200 hover:bg-green-300';
    }
};
const getDragColor = (status) => {
    switch (status) {
        case TEACHER_PREFERENCE_STATUS.PREFER_AWAY: return 'bg-orange-400';
        case TEACHER_PREFERENCE_STATUS.CANNOT: return 'bg-red-400';
        case TEACHER_PREFERENCE_STATUS.FREE: default: return 'bg-green-400';
    }
};

// Calculate hourly slots based on the schedule range for this component's grid
const hourlySlots = allTimeSlotsConfig.filter(slot => {
    const minutes = timeToMinutes(slot);
    return minutes !== null && minutes >= SCHEDULE_START_MINUTE && minutes < SCHEDULE_END_MINUTE;
});
const sortedHourlySlots = [...hourlySlots].sort((a, b) => {
    const aMinutes = timeToMinutes(a) || 0;
    const bMinutes = timeToMinutes(b) || 0;
    return aMinutes - bMinutes;
});

// --- Component ---
function TeacherPreferenceGrid({ preferences, onPreferencesChange, overlaySchedule = [], onScheduleHover }) {
    const gridRef = useRef(null);

    // Get active days from AssetManager
    const { activeDays, loading: daysLoading } = useActiveDays();

    // --- Drag State ---
    const [isDragging, setIsDragging] = useState(false);
    const isDraggingRef = useRef(false);
    const [dragStartSlotKey, setDragStartSlotKey] = useState(null);
    const [dragCurrentSlotKey, setDragCurrentSlotKey] = useState(null);
    const [dragTargetStatus, setDragTargetStatus] = useState(null); // The status to apply
    const [hasDragged, setHasDragged] = useState(false);

    useEffect(() => {
        isDraggingRef.current = isDragging;
    }, [isDragging]);

    const getSlotsInDragRange = useCallback(() => {
        const affectedSlots = new Set();
        if (!isDraggingRef.current || !dragStartSlotKey || !dragCurrentSlotKey) {
            return affectedSlots;
        }
        const start = parseSlotKey(dragStartSlotKey);
        const current = parseSlotKey(dragCurrentSlotKey);

        if (!start.day || !current.day || start.day !== current.day || start.slotMinutes === null || current.slotMinutes === null) {
            if (dragStartSlotKey) affectedSlots.add(dragStartSlotKey);
            return affectedSlots;
        }
        const day = start.day;
        const minMinutes = Math.min(start.slotMinutes, current.slotMinutes);
        const maxMinutes = Math.max(start.slotMinutes, current.slotMinutes);

        sortedHourlySlots.forEach(slotTime => {
            const slotMinutes = timeToMinutes(slotTime);
            if (slotMinutes !== null && slotMinutes >= minMinutes && slotMinutes <= maxMinutes) {
                affectedSlots.add(`${day}-${slotTime}`);
            }
        });
        return affectedSlots;
    }, [dragStartSlotKey, dragCurrentSlotKey]);

    const currentlyDraggedSlots = getSlotsInDragRange();

    // --- Function to get schedule entries for a specific slot ---
    const getScheduleForSlot = useCallback((day, slotTime) => {
        if (!overlaySchedule || overlaySchedule.length === 0) return [];

        const slotMinutes = timeToMinutes(slotTime);
        if (slotMinutes === null) return [];

        const slotEndMinutes = slotMinutes + 60; // Each slot is 1 hour

        return overlaySchedule.filter(entry => {
            // Check if entry matches the day
            if (entry.day?.toLowerCase() !== day?.toLowerCase()) return false;

            // Parse entry time
            const entryStartMinutes = timeToMinutes(entry.start_time || entry.timeStart);
            const entryEndMinutes = timeToMinutes(entry.end_time || entry.timeEnd);

            if (entryStartMinutes === null || entryEndMinutes === null) return false;

            // Check if the entry overlaps with this slot
            return entryStartMinutes < slotEndMinutes && entryEndMinutes > slotMinutes;
        });
    }, [overlaySchedule]);

    // --- Function to build preference ranges from a slot-by-slot status map ---
    const buildRangesFromSlotMap = useCallback((slotStatusMap) => {
        const newPreferenceRanges = [];
        const sortedDays = [...activeDays].sort((a, b) => activeDays.indexOf(a) - activeDays.indexOf(b));

        for (const day of sortedDays) {
            let currentRangeStartSlotTime = null;
            let currentRangeStatus = null;

            for (let i = 0; i < sortedHourlySlots.length; i++) {
                const slotTime = sortedHourlySlots[i];
                const slotDayMap = slotStatusMap.get(day);
                const slotStatus = slotDayMap?.get(slotTime) || TEACHER_PREFERENCE_STATUS.FREE;

                if (slotStatus !== TEACHER_PREFERENCE_STATUS.FREE) {
                    if (currentRangeStartSlotTime === null) { // Start of a new non-free range
                        currentRangeStartSlotTime = slotTime;
                        currentRangeStatus = slotStatus;
                    } else if (slotStatus !== currentRangeStatus) { // Status changed, close previous range
                        const prevSlotTime = sortedHourlySlots[i - 1];
                        const endTimeMinutes = timeToMinutes(prevSlotTime) + 60; // End of the previous slot
                        newPreferenceRanges.push({
                            day: day,
                            startTime: currentRangeStartSlotTime,
                            endTime: formatTimeFromMinutes(endTimeMinutes),
                            status: currentRangeStatus
                        });
                        currentRangeStartSlotTime = slotTime; // Start new range
                        currentRangeStatus = slotStatus;
                    }
                    // If status is the same, continue extending current range
                } else { // Slot is FREE
                    if (currentRangeStartSlotTime !== null) { // End of a non-free range
                        const prevSlotTime = sortedHourlySlots[i - 1];
                        const endTimeMinutes = timeToMinutes(prevSlotTime) + 60;
                        newPreferenceRanges.push({
                            day: day,
                            startTime: currentRangeStartSlotTime,
                            endTime: formatTimeFromMinutes(endTimeMinutes),
                            status: currentRangeStatus
                        });
                        currentRangeStartSlotTime = null;
                        currentRangeStatus = null;
                    }
                }
            }
            // After iterating all slots for the day, close any open range
            if (currentRangeStartSlotTime !== null) {
                const lastSlotTime = sortedHourlySlots[sortedHourlySlots.length - 1];
                const endTimeMinutes = timeToMinutes(lastSlotTime) + 60;
                newPreferenceRanges.push({
                    day: day,
                    startTime: currentRangeStartSlotTime,
                    endTime: formatTimeFromMinutes(endTimeMinutes),
                    status: currentRangeStatus
                });
            }
        }
        return newPreferenceRanges;
    }, [activeDays]);

    // --- MODIFIED: finalizeAction to use buildRangesFromSlotMap ---
    const finalizeAction = useCallback(() => {
        if (isDraggingRef.current) {
            // 1. Initialize slotStatusMap from current `preferences` prop
            const initialSlotStatusMap = new Map();
            for (const day of activeDays) {
                const dayMap = new Map();
                for (const slotTime of sortedHourlySlots) {
                    const slotMinutes = timeToMinutes(slotTime);
                    const status = getStatusForSlot(day, slotMinutes, preferences, TEACHER_PREFERENCE_STATUS);
                    dayMap.set(slotTime, status);
                }
                initialSlotStatusMap.set(day, dayMap);
            }

            // 2. Determine which slots were affected and what the new status is
            const slotsToUpdate = hasDragged ? currentlyDraggedSlots : (dragStartSlotKey ? new Set([dragStartSlotKey]) : new Set());
            const statusToApply = dragTargetStatus;

            // 3. Apply the change to a copy of the slotStatusMap
            const updatedSlotStatusMap = new Map();
            initialSlotStatusMap.forEach((timeMap, day) => {
                updatedSlotStatusMap.set(day, new Map(timeMap));
            });

            slotsToUpdate.forEach(slotKey => {
                const { day, time: slotTimeStr } = parseSlotKey(slotKey);
                if (day && slotTimeStr && statusToApply !== null && updatedSlotStatusMap.has(day)) {
                    updatedSlotStatusMap.get(day).set(slotTimeStr, statusToApply);
                }
            });

            // 4. Build new consolidated ranges from the updated slotStatusMap
            const newRanges = buildRangesFromSlotMap(updatedSlotStatusMap);

            // 5. Call parent with new ranges
            if (typeof onPreferencesChange === 'function') {
                onPreferencesChange(newRanges);
            } else {
                console.error("onPreferencesChange prop is not a function!");
            }
        }

        // Reset state
        setIsDragging(false);
        setDragStartSlotKey(null);
        setDragCurrentSlotKey(null);
        setDragTargetStatus(null);
        setHasDragged(false);
    }, [
        preferences, onPreferencesChange, hasDragged, currentlyDraggedSlots,
        dragStartSlotKey, dragTargetStatus, buildRangesFromSlotMap, activeDays
    ]);

    // --- Event Handlers ---
    const handleMouseUp = (slotKey) => {
        const { day, slotMinutes } = parseSlotKey(slotKey);
        if (day === null || slotMinutes === null) return;

        if (!hasDragged) {
            // Determine the target status for this interaction (click or start of drag)
            const currentSlotDisplayStatus = getStatusForSlot(day, slotMinutes, preferences, TEACHER_PREFERENCE_STATUS);
            let nextStatus = currentSlotDisplayStatus;
            if (currentSlotDisplayStatus === TEACHER_PREFERENCE_STATUS.PREFER_AWAY) {
                nextStatus = TEACHER_PREFERENCE_STATUS.CANNOT;
            } else if (currentSlotDisplayStatus === TEACHER_PREFERENCE_STATUS.CANNOT) {
                nextStatus = TEACHER_PREFERENCE_STATUS.FREE;
            } else { // Covers FREE
                nextStatus = TEACHER_PREFERENCE_STATUS.PREFER_AWAY;
            }

            setDragStartSlotKey(slotKey);
            setDragCurrentSlotKey(slotKey);
            setDragTargetStatus(nextStatus);
            setHasDragged(false);
        }
    };

    const handleMouseDown = (slotKey) => {
        const { day, slotMinutes } = parseSlotKey(slotKey);
        if (day === null || slotMinutes === null) return;

        const currentSlotDisplayStatus = getStatusForSlot(day, slotMinutes, preferences, TEACHER_PREFERENCE_STATUS);
        let nextStatus = currentSlotDisplayStatus;

        setIsDragging(true);
        setDragStartSlotKey(slotKey);
        setDragCurrentSlotKey(slotKey);
        setDragTargetStatus(nextStatus);
        setHasDragged(false);
    };

    const handleMouseEnter = (slotKey) => {
        if (!isDraggingRef.current) return;
        setDragCurrentSlotKey(slotKey);
        if (slotKey !== dragStartSlotKey) {
            setHasDragged(true);
        }
    };

    // Global mouseup listener
    useEffect(() => {
        const handleGlobalMouseUp = () => {
            if (isDraggingRef.current) {
                finalizeAction();
            }
        };
        window.addEventListener('mouseup', handleGlobalMouseUp);
        return () => {
            window.removeEventListener('mouseup', handleGlobalMouseUp);
        };
    }, [finalizeAction]);

    // Don't render if still loading days configuration
    if (daysLoading) {
        return <div className="text-center text-gray-500 py-4">Loading schedule configuration...</div>;
    }

    // Calculate the number of columns dynamically: 1 for time + number of active days
    // Use explicit Tailwind classes for grid columns to ensure they're available
    const getGridColsClass = (numDays) => {
        const totalCols = numDays + 1; // +1 for time column
        switch (totalCols) {
            case 6: return 'grid-cols-6'; // 5 days + time
            case 7: return 'grid-cols-7'; // 6 days + time
            case 8: return 'grid-cols-8'; // 7 days + time
            case 5: return 'grid-cols-5'; // 4 days + time
            case 4: return 'grid-cols-4'; // 3 days + time
            case 3: return 'grid-cols-3'; // 2 days + time
            case 2: return 'grid-cols-2'; // 1 day + time
            default: return 'grid-cols-6'; // Fallback to 6 columns
        }
    };

    return (
        <div
            ref={gridRef}
            className={`grid ${getGridColsClass(activeDays.length)} gap-px border border-gray-300 bg-gray-300 select-none cursor-default`}
            onMouseDown={(e) => e.preventDefault()}
        >
            {/* Header Row */}
            <div className="bg-gray-200 p-1 text-xs font-semibold text-center sticky top-0 z-10">Time</div>
            {activeDays.map(day => (
                <div key={day} className="bg-gray-200 p-1 text-xs font-semibold text-center capitalize sticky top-0 z-10">{day}</div>
            ))}

            {/* Grid Cells */}
            {sortedHourlySlots.map(slotTime => (
                <React.Fragment key={slotTime}>
                    <div className="bg-gray-100 p-1 text-xs text-center font-medium flex items-center justify-center h-8">{slotTime}</div>
                    {activeDays.map(day => {
                        const slotKey = `${day}-${slotTime}`;
                        const slotMinutes = timeToMinutes(slotTime);
                        // Determine display status: if dragging, use dragTargetStatus for affected slots
                        const isSlotInDragRange = isDragging && currentlyDraggedSlots.has(slotKey);
                        const displayStatus = isSlotInDragRange
                            ? dragTargetStatus
                            : getStatusForSlot(day, slotMinutes, preferences, TEACHER_PREFERENCE_STATUS);

                        const displayColorClass = isSlotInDragRange
                            ? getDragColor(displayStatus)
                            : getStatusColor(displayStatus);

                        const titleText = (displayStatus || 'free').replace('_', ' ');

                        // Get schedule entries for this slot
                        const scheduleEntries = getScheduleForSlot(day, slotTime);
                        const hasSchedule = scheduleEntries.length > 0;

                        // Build tooltip with schedule info
                        let tooltip = `${day.charAt(0).toUpperCase() + day.slice(1)} ${slotTime} - ${titleText}`;
                        if (hasSchedule) {
                            tooltip += '\n\nScheduled:';
                            scheduleEntries.forEach(entry => {
                                const subject = entry.subject_name || entry.course || entry.subject || 'Class';
                                const time = entry.start_time && entry.end_time
                                    ? `${entry.start_time}-${entry.end_time}`
                                    : '';
                                tooltip += `\n• ${subject} ${time}`;
                            });
                        }

                        return (
                            <div
                                key={slotKey}
                                className={`p-1 h-8 cursor-pointer transition-colors duration-100 ${displayColorClass} relative`}
                                title={tooltip}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleMouseDown(slotKey);
                                }}
                                onMouseUp={(e) => {
                                    e.preventDefault();
                                    handleMouseUp(slotKey);
                                }}
                                onMouseEnter={() => handleMouseEnter(slotKey)}
                            >
                                {hasSchedule && (
                                    <div
                                        className="absolute inset-0 flex items-center justify-center pointer-events-auto"
                                        onMouseEnter={() => {
                                            if (onScheduleHover && scheduleEntries.length > 0) {
                                                const entryIds = scheduleEntries.map(e => e.id).filter(Boolean);
                                                onScheduleHover(entryIds);
                                            }
                                        }}
                                        onMouseLeave={() => {
                                            if (onScheduleHover) {
                                                onScheduleHover([]);
                                            }
                                        }}
                                    >
                                        <div className="w-full h-full border-2 border-blue-500 bg-blue-500 bg-opacity-30 flex items-center justify-center">
                                            <span className="text-[8px] font-bold text-blue-900 leading-tight text-center px-0.5">
                                                {scheduleEntries.map(e => e.subject_name || e.course || e.subject).filter(Boolean).join(', ').substring(0, 10)}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </React.Fragment>
            ))}
        </div>
    );
}

export default TeacherPreferenceGrid;